import { PropType } from 'vue';
import i18n from '@/core/translations/i18n';
import { Vue, Options } from 'vue-class-component';
import {
  DeviceDTO,
  SlotStatus,
  SystemType,
  FunctionType,
  ComponentType,
  PlantDetailDTO,
  DeviceProximity,
  DeviceBrightness,
  DeviceIconsVisibility,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';

import ModuleItem from './ModuleItem/ModuleItem.vue';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { cloneObject, getRoomIconFromType } from '@/core/utils/utils';
import PlantDevicesItem from './PlantDevicesItem/PlantDevicesItem.vue';
import PlantDevicesAnomaly from './PlantDevicesAnomaly/PlantDevicesAnomaly.vue';
import PlantDevicesSettings from './PlantDevicesSettings/PlantDevicesSettings.vue';
import TabsEnumFilter from '../../../components/TabsEnumFilter/TabsEnumFilter.vue';
import { mapGetters } from 'vuex';

enum TabFilters {
  All,
  Available,
  Configured,
  Ignored,
  Anomalies,
}

type SelectButtonValue = {
  label: string;
  value: FunctionType | ComponentType | SystemType | string;
};

interface ExtendedDeviceDTO extends DeviceDTO {
  isSelected: boolean;
}

const allSelectButton: SelectButtonValue = { label: 'All', value: '' };

@Options({
  name: 'PlantDevices',
  emits: ['add-object', 'add-structure', 'update-list'],
  components: {
    ModuleItem,
    TabsEnumFilter,
    PlantDevicesItem,
    PlantDevicesSettings,
    PlantDevicesAnomaly,
  },
  props: {
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  computed: {
    ...mapGetters('UI', [
      'devicesPageAddDeviceEnabled',
      'devicesPageFilterZigbeeEnabled',
    ]),
  },
})
export default class PlantDevices extends Vue {
  TabFilters = TabFilters;
  selectedTab = TabFilters.All;
  currentPlant?: PlantDetailDTO;
  plantDevices: ExtendedDeviceDTO[] = [];
  getRoomIconFromType = getRoomIconFromType;
  devicesPageFilterZigbeeEnabled!: boolean;
  isVisibleDeviceFiltersModal: boolean = false;
  selectedDevice: ExtendedDeviceDTO | null = null;

  tabsFilters = [
    { label: i18n.global.t('AppResource.All'), value: TabFilters.All },
    { label: i18n.global.t('AppResource.Available'), value: TabFilters.Available },
    {
      label: i18n.global.t('AppResource.Configured'),
      value: TabFilters.Configured,
    },
    { label: i18n.global.t('AppResource.Ignored'), value: TabFilters.Ignored },
    { label: i18n.global.t('AppResource.Anomalies'), value: TabFilters.Anomalies },
  ];

  functionList: SelectButtonValue[] = [
    allSelectButton,
    { label: i18n.global.t('AppResource.Thermo'), value: FunctionType.Thermo },
    {
      label: i18n.global.t('AppResource.Automation'),
      value: FunctionType.Automation,
    },
    { label: i18n.global.t('AppResource.Energy'), value: FunctionType.Energy },
    {
      label: i18n.global.t('AppResource.Security'),
      value: FunctionType.Security,
    },
  ];
  selectedFunction: SelectButtonValue = allSelectButton;

  get systemList(): SelectButtonValue[] {
    const list = [allSelectButton, { label: 'SCS', value: SystemType.Scs }];
    if (this.devicesPageFilterZigbeeEnabled)
      list.push({ label: 'ZigBee', value: SystemType.Zigbee });
    return list;
  }
  selectedSystem: SelectButtonValue = allSelectButton;

  componetTypeList = [
    allSelectButton,
    {
      label: i18n.global.t('AppResource.Actuator'),
      value: ComponentType.Actuator,
    },
    {
      label: i18n.global.t('AppResource.Command'),
      value: ComponentType.Command,
    },
  ];
  selectedComponentType: SelectButtonValue = allSelectButton;

  get slotsTitle() {
    return this.$t('AppResource.Modules');
  }

  get backLink() {
    return {
      name: 'page-plant-dashboard',
      params: this.$route.params,
    };
  }

  async mounted() {
    await this.fetchDevices();
  }

  get selectedDeviceSlots() {
    return this.selectedDevice?.slots?.filter((s) => !s.isSetting);
  }

  get filteredDevices() {
    let _plantDevices = cloneObject(this.plantDevices);

    // Tabs
    if (this.selectedTab === TabFilters.Ignored) {
      _plantDevices = this.plantDevices.filter((d) => d.isIgnored);
    }

    if (this.selectedTab === TabFilters.Configured) {
      _plantDevices = this.plantDevices.filter(
        (d) =>
          d.slots && !d.slots.some((s) => s.status === SlotStatus.NotConfigured)
      );
    }

    if (this.selectedTab === TabFilters.Available) {
      _plantDevices = this.plantDevices.filter(
        (d) =>
          d.slots && d.slots.some((s) => s.status === SlotStatus.NotConfigured)
      );
    }

    if (this.selectedTab === TabFilters.Anomalies) {
      _plantDevices = this.plantDevices.filter((d) => d.isAnomaly === true);
    }

    // Function Type
    if (this.selectedFunction.value !== '') {
      _plantDevices = _plantDevices.filter(
        (d) => d.functionType === this.selectedFunction?.value
      );
    }

    // Componennt Type
    if (this.selectedComponentType.value !== '') {
      _plantDevices = _plantDevices.filter((d) =>
        d.componentTypes?.includes(
          this.selectedComponentType.value as ComponentType
        )
      );
    }

    // System Type
    if (this.selectedSystem.value !== '') {
      _plantDevices = _plantDevices.filter(
        (d) => d.systemType === this.selectedSystem?.value
      );
    }

    return _plantDevices;
  }

  showDeviceFiltersModal() {
    this.isVisibleDeviceFiltersModal = true;
  }

  hideDeviceFiltersModal() {
    this.isVisibleDeviceFiltersModal = false;
  }

  async fetchDevices() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.plantDevices = (
        await plantsClient.allDevices(this.currentPlant?.desktopId as number)
      ).map((k) => ({ ...k, isSelected: false } as ExtendedDeviceDTO));
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  goToDeviceDetail(device: ExtendedDeviceDTO) {
    this.unselectDevice();
    const dev = this.plantDevices.find((k) => k.uid === device.uid);
    if (!dev) return;
    dev.isSelected = true;

    dev.deviceBrightness =
      dev.deviceBrightness === DeviceBrightness.None
        ? DeviceBrightness.Min
        : dev.deviceBrightness;
    dev.deviceProximity =
      dev.deviceProximity === DeviceProximity.None
        ? DeviceProximity.Max
        : dev.deviceProximity;
    dev.deviceIconsVisibility =
      dev.deviceIconsVisibility === DeviceIconsVisibility.None
        ? DeviceIconsVisibility.AtTouch
        : dev.deviceIconsVisibility;
    dev.ledIntensity = device.ledIntensity! * 10;

    this.selectedDevice = dev;
  }

  unselectDevice() {
    if (!this.selectedDevice) return;
    this.selectedDevice.ledIntensity = this.selectedDevice.ledIntensity! / 10;
    this.selectedDevice.isSelected = false;
    this.selectedDevice = null;
  }

  onSelectTab(tabenum: TabFilters) {
    this.selectedTab = tabenum;
  }
}
